import { Button, MenuItem, Select, TextField, type DialogProps } from '@mui/material';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { object, string } from 'yup';
import { useCreatePlaylist } from '~/api/playlists';
import {
  Dialog,
  DialogContentTitle,
  DialogForm,
  DialogTitle,
} from '~/components/dialogs/components';
import { DialogActions, DialogContent } from '~/components/dialogs/lib';
import { useAppContext } from '~/contexts';
import { useLink } from '~/hooks/link';
import { i18n } from '~/lib/i18n';

const validationSchema = object({
  description: string().optional(),
  kind: string().default(''),
  name: string()
    .required(i18n.t('pages.playlists.new_dialog.validation.name_required'))
    .label(i18n.t('common.labels.name')),
});

export const NewPlaylistDialog = (props: DialogProps) => {
  const { t } = useTranslation();
  const { currentNetwork, currentUser } = useAppContext();
  const params = useParams<{ playlistFolderId: string }>();
  const playlistFolderId = Number(params.playlistFolderId);

  const navigate = useNavigate();

  const link = useLink();

  const [createPlaylist] = useCreatePlaylist();

  // Sucks
  const close = () => props.onClose?.({}, 'backdropClick');

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      description: '',
      kind: '',
      name: '',
    },
    onSubmit: async (input) => {
      const { data, errors } = await createPlaylist({
        variables: {
          input: {
            ...input,
            networkId: currentNetwork.id,
            reusable: true,
            playlistFolderId,
          },
        },
      });
      if (!errors?.length) close();
      navigate(
        link(
          `/playlists/${data?.createPlaylist?.playlist?.playlistFolderId}/playlist/${data?.createPlaylist?.playlist?.id}`,
        ),
      );
    },
    validateOnBlur: true,
    validationSchema,
  });

  return (
    <Dialog {...props}>
      <DialogTitle onClose={close}>{t('pages.playlists.new_dialog.title')}</DialogTitle>

      <DialogContent>
        <DialogContentTitle>{t('pages.playlists.new_dialog.content')}</DialogContentTitle>

        <DialogForm id="new-playlist" onSubmit={formik.handleSubmit}>
          <label>
            <span className={`label required`}>{t('common.labels.name')}</span>
            <TextField
              autoFocus
              error={formik.touched.name && !!formik.errors.name}
              fullWidth
              helperText={(formik.touched.name && formik.errors.name) || ' '}
              name="name"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.name}
            />
          </label>

          <label>
            <span className={`label`}>{t('common.labels.description')}</span>
            <TextField
              error={formik.touched.description && !!formik.errors.description}
              fullWidth
              helperText={(formik.touched.description && formik.errors.description) || ' '}
              minRows={4}
              maxRows={4}
              multiline
              name="description"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.description}
            />
          </label>

          {currentUser.admin && (
            <label>
              <span className={`label`}>{t('pages.playlists.new_dialog.labels.kind')}</span>
              <Select
                displayEmpty
                fullWidth
                name="kind"
                onChange={formik.handleChange}
                value={formik.values.kind}
              >
                <MenuItem value="">{t('pages.playlists.new_dialog.kind_options.content')}</MenuItem>
                <MenuItem value="ad">
                  {t('pages.playlists.new_dialog.kind_options.sponsorship')}
                </MenuItem>
              </Select>
            </label>
          )}
        </DialogForm>
      </DialogContent>

      <DialogActions>
        <Button disabled={formik.isSubmitting} onClick={close} variant="outlined">
          {t('common.actions.cancel')}
        </Button>

        <Button
          color="primary"
          disabled={formik.isSubmitting || !formik.isValid || !formik.dirty}
          form="new-playlist"
          type="submit"
          variant="contained"
        >
          {t('common.actions.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
